import React, { useState, useMemo } from "react";
import {
  TrendingUp, Search, Plus, AlertTriangle, CheckCircle2,
  ExternalLink, Sparkles, Filter, Globe, ArrowUpRight,
  ShieldAlert, RefreshCw, X, Trash2
} from "lucide-react";
import { SeoMarket, KeywordItem, ContentItem } from "./types";

interface SeoKeywordsTabProps {
  market: SeoMarket;
  keywordsList: KeywordItem[];
  setKeywordsList: React.Dispatch<React.SetStateAction<KeywordItem[]>>;
  contentList: ContentItem[];
  // Set when the SEO engine backend is connected: keywords are tracked server-side.
  engine?: {
    addKeywords: (keywords: string[], intent: KeywordItem["intent"], targetUrl: string) => Promise<void>;
    cannibalization: Array<{ query: string; source: string; pages: Array<{ url: string; impressions?: number }> }>;
  };
}

const SERP_FEATURE_LABEL: Record<string, string> = {
  featured_snippet: "Featured snippet", people_also_ask: "PAA", ai_overview: "AI Overview", local_pack: "Local pack",
  video: "Video", images: "Billeder", top_stories: "Nyheder",
};

export default function SeoKeywordsTab({
  market,
  keywordsList,
  setKeywordsList,
  contentList,
  engine
}: SeoKeywordsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterIntent, setFilterIntent] = useState<string>("all");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Keyword Form
  const [newKeyword, setNewKeyword] = useState("");
  const [newVolume, setNewVolume] = useState<number>(3000);
  const [newDifficulty, setNewDifficulty] = useState<number>(45);
  const [newTargetUrl, setNewTargetUrl] = useState("/samla-lan");
  const [newIntent, setNewIntent] = useState<KeywordItem["intent"]>("transactional");
  const [newPos, setNewPos] = useState<number>(15);

  const marketKeywords = keywordsList.filter(k => k.market === market);

  // Cannibalization check: multiple articles targeting the same keyword
  const cannibalizationWarnings = useMemo(() => {
    const counts: Record<string, string[]> = {};
    contentList
      .filter(c => c.market === market)
      .forEach(item => {
        const kw = item.targetKeyword.trim().toLowerCase();
        if (kw) {
          if (!counts[kw]) counts[kw] = [];
          counts[kw].push(item.title);
        }
      });

    return Object.entries(counts).filter(([_, titles]) => titles.length > 1);
  }, [contentList, market]);

  const filteredKeywords = marketKeywords
    .filter(k => filterIntent === "all" || k.intent === filterIntent)
    .filter(k => !searchQuery || k.keyword.toLowerCase().includes(searchQuery.toLowerCase()) || k.targetUrl.toLowerCase().includes(searchQuery.toLowerCase()));

  const handleAddKeyword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKeyword.trim()) return;

    if (engine) {
      await engine.addKeywords([newKeyword.trim()], newIntent, newTargetUrl.trim());
      setIsAddModalOpen(false);
      setNewKeyword("");
      return;
    }

    const newItem: KeywordItem = {
      id: `kw-${Date.now()}`,
      market,
      keyword: newKeyword.trim(),
      currentPosition: Number(newPos) || null,
      previousPosition: Number(newPos) ? Number(newPos) + 2 : null,
      change: 2,
      searchVolume: Number(newVolume) || 1000,
      difficulty: Number(newDifficulty) || 40,
      targetUrl: newTargetUrl.trim() || "/",
      intent: newIntent,
      status: "rising",
      lastChecked: new Date().toISOString().split("T")[0]
    };

    setKeywordsList(prev => [newItem, ...prev]);
    setIsAddModalOpen(false);
    setNewKeyword("");
  };

  const handleDeleteKeyword = (id: string) => {
    if (window.confirm("Vil du fjerne dette søgeord fra overvågning?")) {
      setKeywordsList(prev => prev.filter(k => k.id !== id));
    }
  };

  return (
    <div className="space-y-6">

      {/* Free Google Search Console Setup Guide & Integration Status */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🎯</span>
              <h3 className="font-bold text-gray-900 text-base">
                Google Search Console (100% Gratis SERP & Rank Tracking)
              </h3>
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-800">
                Officiel Google Kilde
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              GSC giver dig de faktiske søgeord, som folk i Sverige og Norge finder SimplyLoan på, uden at betale for dyre rank trackers.
            </p>
          </div>

          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noreferrer"
            className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition flex items-center gap-1.5 shrink-0"
          >
            <span>Åbn Google Search Console</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>

        {/* 3-Step Verification Checklist */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
            <div className="font-bold text-gray-900 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-[#0F3F34] text-white flex items-center justify-center text-[10px]">1</span>
              <span>Cloudflare DNS TXT</span>
            </div>
            <p className="text-gray-500">
              Verificér domænet <code>{market === "NO" ? "simplyloan.no" : "simplyloan.se"}</code> via en TXT-record i Cloudflare for 100% dækning af alle subdomæner.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
            <div className="font-bold text-gray-900 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-[#0F3F34] text-white flex items-center justify-center text-[10px]">2</span>
              <span>Indsend Sitemap.xml</span>
            </div>
            <p className="text-gray-500">
              Indsend <code>https://{market === "NO" ? "simplyloan.no" : "simplyloan.se"}/sitemap.xml</code> så Googlebot crawler alle dine undersider.
            </p>
          </div>

          <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1">
            <div className="font-bold text-gray-900 flex items-center gap-1.5">
              <span className="w-5 h-5 rounded-full bg-[#0F3F34] text-white flex items-center justify-center text-[10px]">3</span>
              <span>Live SERP & Clicks</span>
            </div>
            <p className="text-gray-500">
              Følg dine gennemsnitlige placeringer, kliks og CTR på de vigtigste lånesøgeord helt uden abonnementsomkostninger.
            </p>
          </div>
        </div>
      </div>

      {/* Cannibalization Warning if any */}
      {cannibalizationWarnings.length > 0 && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs space-y-2">
          <div className="font-bold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Advarsel: Mulig Søgeords-Kannibalisering opdaget i {market}!</span>
          </div>
          <p className="text-amber-800">
            Følgende søgeord har 2 eller flere artikler i din pipeline, hvilket kan få siderne til at konkurrere mod hinanden i Google:
          </p>
          <ul className="list-disc list-inside space-y-1 font-medium">
            {cannibalizationWarnings.map(([kw, titles]) => (
              <li key={kw}>
                <strong>&quot;{kw}&quot;</strong> deles af: {titles.join(" OG ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {engine && engine.cannibalization.length > 0 && (
        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-amber-900 text-xs space-y-2">
          <div className="font-bold text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Flere af vores sider konkurrerer i Google ({engine.cannibalization.length})</span>
          </div>
          <p className="text-amber-800">
            Fundet i Search Console (to sider med hver mindst 15 % af visningerne) eller i SERP-historikken (rangerende
            URL skifter). Saml indholdet på én side, og lad den anden linke til den.
          </p>
          <ul className="space-y-1">
            {engine.cannibalization.slice(0, 8).map((c) => (
              <li key={`${c.source}-${c.query}`}>
                <strong>&quot;{c.query}&quot;</strong>
                <span className="text-amber-700"> ({c.source === "gsc" ? "Search Console" : "SERP"}): </span>
                {c.pages.map((p) => p.url.replace(/^https?:\/\/[^/]+/, "")).join(" · ")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="bg-white p-4 rounded-2xl border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
        
        <div className="flex items-center gap-2 flex-1 max-w-md bg-gray-50 px-3 py-2 rounded-xl border border-gray-200">
          <Search className="w-4 h-4 text-gray-400 shrink-0" />
          <input
            type="text"
            placeholder="Søg i overvågede søgeord, landingssider..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full text-sm bg-transparent border-0 focus:outline-none p-0 text-gray-800 placeholder-gray-400"
          />
        </div>

        <div className="flex items-center gap-2">
          <select
            value={filterIntent}
            onChange={(e) => setFilterIntent(e.target.value)}
            className="text-xs bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 font-medium text-gray-700 focus:outline-none"
          >
            <option value="all">Al søge-intention</option>
            <option value="transactional">Transaktionel (Lån nu)</option>
            <option value="commercial">Kommerciel (Sammenlign)</option>
            <option value="informational">Informationel (Hvad er)</option>
          </select>

          <button
            onClick={() => setIsAddModalOpen(true)}
            className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white text-xs font-bold rounded-xl transition shadow-xs"
          >
            <Plus className="w-4 h-4" />
            <span>Tilføj søgeord</span>
          </button>
        </div>
      </div>

      {/* Keywords Table */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-gray-600">
            <thead className="bg-gray-50/80 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100">
              <tr>
                <th className="py-3.5 px-5">Søgeord</th>
                <th className="py-3.5 px-4">Position</th>
                <th className="py-3.5 px-4">Månedlig Volumen</th>
                <th className="py-3.5 px-4">Difficulty (KD)</th>
                <th className="py-3.5 px-4">Intention</th>
                <th className="py-3.5 px-4">Målside på SimplyLoan</th>
                <th className="py-3.5 px-5 text-right">Handlinger</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredKeywords.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-gray-400">
                    Ingen søgeord fundet for {market}.
                  </td>
                </tr>
              ) : (
                filteredKeywords.map((kw) => (
                  <tr key={kw.id} className="hover:bg-gray-50/60 transition-colors">
                    
                    <td className="py-4 px-5">
                      <div className="font-bold text-gray-900">{kw.keyword}</div>
                      <div className="text-xs text-gray-400 mt-0.5">Sidst tjekket: {kw.lastChecked}</div>
                      {(kw.serpFeatures?.length || kw.gscImpressions) ? (
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {kw.serpFeatures?.map((f) => SERP_FEATURE_LABEL[f] || f).join(" · ")}
                          {kw.gscImpressions ? `${kw.serpFeatures?.length ? " · " : ""}GSC 28 d: ${kw.gscClicks ?? 0} klik / ${kw.gscImpressions.toLocaleString("da-DK")} visninger` : ""}
                        </div>
                      ) : null}
                    </td>

                    <td className="py-4 px-4">
                      {kw.currentPosition ? (
                        <div className="flex items-center gap-1.5">
                          <span className="font-mono font-bold text-[#0F3F34] bg-emerald-50 px-2 py-0.5 rounded text-xs">
                            #{kw.currentPosition}
                          </span>
                          {kw.change > 0 ? (
                            <span className="text-[11px] font-bold text-emerald-600">▲ +{kw.change}</span>
                          ) : kw.change < 0 ? (
                            <span className="text-[11px] font-bold text-red-500">▼ {kw.change}</span>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">
                          {engine && kw.lastChecked === "–" ? "Ikke målt endnu" : "Ikke i top 100"}
                        </span>
                      )}
                    </td>

                    <td className="py-4 px-4 font-semibold text-gray-800">
                      {kw.searchVolume.toLocaleString('da-DK')} / md.
                    </td>

                    <td className="py-4 px-4">
                      {engine && !kw.difficulty ? (
                        <span className="text-xs text-gray-400">–</span>
                      ) : (
                        <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                          kw.difficulty < 35 ? 'bg-emerald-100 text-emerald-800' :
                          kw.difficulty < 65 ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'
                        }`}>
                          KD {kw.difficulty}
                        </span>
                      )}
                    </td>

                    <td className="py-4 px-4">
                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 capitalize">
                        {kw.intent}
                      </span>
                    </td>

                    <td className="py-4 px-4 font-mono text-xs text-[#0F3F34]">
                      {kw.targetUrl}
                    </td>

                    <td className="py-4 px-5 text-right">
                      {!engine && (
                        <button
                          onClick={() => handleDeleteKeyword(kw.id)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Slet søgeord"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Free Tools Ribbon */}
      <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 flex flex-wrap items-center justify-between gap-3 text-xs">
        <div className="flex items-center gap-2 text-gray-600">
          <Sparkles className="w-4 h-4 text-[#0F3F34]" />
          <span>Gratis research værktøjer:</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="https://trends.google.com"
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 bg-white border border-gray-200 hover:border-gray-300 rounded-lg text-gray-700 font-bold transition flex items-center gap-1"
          >
            <span>Google Trends</span>
            <ExternalLink className="w-3 h-3 text-gray-400" />
          </a>
          <a
            href="https://search.google.com/search-console"
            target="_blank"
            rel="noreferrer"
            className="px-3 py-1.5 bg-white border border-gray-200 hover:border-gray-300 rounded-lg text-gray-700 font-bold transition flex items-center gap-1"
          >
            <span>Search Console</span>
            <ExternalLink className="w-3 h-3 text-gray-400" />
          </a>
        </div>
      </div>

      {/* Modal: Tilføj søgeord */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-gray-100">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#0F3F34]" />
                <span>Overvåg nyt søgeord ({market})</span>
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddKeyword} className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Søgeord *</label>
                <input
                  type="text"
                  required
                  placeholder="f.eks. samla lån med betalningsanmärkning"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              {engine && (
                <p className="text-gray-500">
                  Volumen, sværhedsgrad og placering hentes automatisk (DataForSEO og Search Console).
                </p>
              )}

              {!engine && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Est. Månedlig Volumen</label>
                  <input
                    type="number"
                    value={newVolume}
                    onChange={(e) => setNewVolume(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Sværhedsgrad (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={newDifficulty}
                    onChange={(e) => setNewDifficulty(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  />
                </div>
              </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Mål URL Path</label>
                  <input
                    type="text"
                    value={newTargetUrl}
                    onChange={(e) => setNewTargetUrl(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Søge-Intention</label>
                  <select
                    value={newIntent}
                    onChange={(e) => setNewIntent(e.target.value as KeywordItem["intent"])}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                  >
                    <option value="transactional">Transaktionel</option>
                    <option value="commercial">Kommerciel</option>
                    <option value="informational">Informationel</option>
                  </select>
                </div>
              </div>

              {!engine && (
              <div>
                <label className="block font-bold text-gray-700 mb-1">Est. Nuværende Placering</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={newPos}
                  onChange={(e) => setNewPos(Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                />
              </div>
              )}

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl transition"
                >
                  Gem søgeord
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
